import React from 'react';
import { Modal, Box, Typography, Button, Divider, CircularProgress } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { useTheme } from '../../hooks/useTheme';
import { capitalizeFirstLetter } from '../../utils/capitalize';
import IconButton from '../shared/IconButton';

export interface ModalProps {
    open: boolean;
    handleClose: () => void;
}

interface ModalBodyProps extends ModalProps {
    title?: string;
    subTitle?: string;
    description?: string;
    handleOk?: () => void;
    handleThird?: () => void;
    okText?: string;
    closeText?: string;
    thirdButton?: boolean;
    thirdText?: string;
    children?: React.ReactNode;
    disabledOk?: boolean;
    disabledThird?: boolean;
    height?: string | number;
    width?: string | number;
    okCanClose?: boolean;
    isOkVisible?: boolean;
    loading?: boolean;
    oKloading?: boolean;
}

const ModalBody: React.FC<ModalBodyProps> = ({
    title,
    subTitle,
    description,
    open,
    handleClose,
    handleOk = () => {},
    handleThird = () => {},
    okText,
    closeText,
    children,
    thirdButton = false,
    thirdText = '',
    disabledOk = false,
    disabledThird = false,
    isOkVisible = true,
    height = 'auto',
    width = '500px',
    okCanClose = true,
    loading = false,
    oKloading = false,
}) => {
    const { t } = useTranslation();
    const { muiTheme } = useTheme();

    return (
        <Modal open={open} onClose={handleClose}>
            <Box
                sx={{
                    position: 'absolute',
                    top: '50%',
                    left: '50%',
                    transform: 'translate(-50%, -50%)',
                    height: { xs: '100%', md: height },
                    width: { xs: '100%', md: width },
                    maxWidth: { xs: '100%', md: '80%' },
                    maxHeight: { xs: '100%', md: '80%' },
                    bgcolor: muiTheme.palette.background.paper,
                    borderRadius: { xs: 0, md: 2 },
                    boxShadow: 24,
                    display: 'flex',
                    flexDirection: 'column',
                    outline: 'none',
                    overflow: 'hidden',
                }}
            >
                <Box
                    sx={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                        p: 2,
                        bgcolor: muiTheme.palette.common.black,
                        color: muiTheme.palette.common.white,
                    }}
                >
                    <Typography variant="h6">{capitalizeFirstLetter(title)}</Typography>
                    <IconButton iconName="close" onClick={handleClose} color="inherit" title={capitalizeFirstLetter(t('close'))} />
                </Box>

                {subTitle && (
                    <Box sx={{ bgcolor: 'grey.900', px: 2, py: 1 }}>
                        <Typography variant="subtitle1" color="white">
                            {capitalizeFirstLetter(subTitle)}
                        </Typography>
                    </Box>
                )}

                {description && (
                    <Box sx={{ bgcolor: 'grey.800', px: 2, py: 1 }}>
                        <Typography variant="body2" color="white">
                            {description}
                        </Typography>
                    </Box>
                )}

                <Box
                    sx={{
                        p: 2,
                        overflowY: 'auto',
                        flexGrow: 1,
                        display: 'flex',
                        flexDirection: 'column',
                        gap: 1,
                    }}
                >
                    {children}
                </Box>

                {(isOkVisible || thirdButton) && <Divider />}

                {/* Actions */}
                {(isOkVisible || thirdButton) && (
                    <Box
                        sx={{
                            display: 'flex',
                            justifyContent: 'flex-end',
                            gap: 1,
                            p: 2,
                        }}
                    >
                        {thirdButton && (
                            <Button
                                variant="outlined"
                                color="error"
                                disabled={disabledThird || loading}
                                onClick={() => {
                                    handleThird();
                                    handleClose();
                                }}
                            >
                                {thirdText}
                            </Button>
                        )}
                        {isOkVisible && (
                            <Button
                                variant="contained"
                                color="success"
                                disabled={disabledOk}
                                onClick={() => {
                                    handleOk();
                                    if (okCanClose) handleClose();
                                }}
                                loading={oKloading}
                            >
                                {okText || capitalizeFirstLetter(t('validate'))}
                            </Button>
                        )}
                    </Box>
                )}
            </Box>
        </Modal>
    );
};

export default ModalBody;
